import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Truck, Warehouse } from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import LottieBlock from '../components/LottieBlock';
import QuoteDialog from '../components/QuoteDialog';
import { FOCUS_CATEGORIES, PRODUCTS } from '../data/products';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [supplyAnim, setSupplyAnim] = useState<object | null>(null);

  useEffect(() => {
    fetch('/lottie/supply.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setSupplyAnim(data))
      .catch(() => undefined);
  }, []);

  const preview = PRODUCTS.slice(0, 6);

  return (
    <div className="va-page">
      <Header />
      <main className="va-main">
        <section className="va-hero">
          <div className="va-hero-copy">
            <p className="va-hero-brand">
              Vaka<span>Africa</span>
            </p>
            <h1 className="va-h1">Mining and construction supply you can schedule around.</h1>
            <p className="va-lede">
              Reagents, acids, and site materials for African operations that need clear specs and dependable delivery.
            </p>
            <div className="va-hero-actions">
              <button type="button" className="va-btn va-btn-primary" onClick={() => navigate('/shop')}>
                View products
                <span className="va-btn-icon">
                  <ArrowUpRight size={14} weight="bold" />
                </span>
              </button>
              <button type="button" className="va-btn va-btn-secondary" onClick={() => setQuoteOpen(true)}>
                Request a quote
              </button>
            </div>
          </div>
          <div className="va-hero-media" aria-hidden="true">
            <img src="/images/hero-mining.jpg" alt="" />
          </div>
        </section>

        <section className="va-section">
          <div className="va-container">
            <Reveal>
              <div className="va-section-head">
                <h2 className="va-h2">Built around the materials your plant actually runs.</h2>
                <p className="va-lede">
                  Focused stock lines for cyanide, caustic, lime, and carbon, plus the acids and assay reagents that keep recovery circuits moving.
                </p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="va-category-strip">
                {FOCUS_CATEGORIES.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    className="va-category-cell"
                    onClick={() => navigate(`/shop?category=${encodeURIComponent(cat.key)}`)}
                    style={{ textAlign: 'left', cursor: 'pointer', border: 'none', font: 'inherit' }}
                  >
                    <strong>{cat.title}</strong>
                    <p>{cat.blurb}</p>
                  </button>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="va-section va-section-tint">
          <div className="va-container">
            <div className="va-split">
              <Reveal>
                <div className="va-section-head" style={{ marginBottom: 0 }}>
                  <h2 className="va-h2">Equipment and chemistry for hard sites.</h2>
                  <p className="va-lede">
                    VakaAfrica supplies mining reagents and construction-adjacent materials with packaging, purity, and handling guidance that procurement teams can act on.
                  </p>
                  <div style={{ display: 'grid', gap: '1rem', marginTop: '1.25rem' }}>
                    <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
                      <Warehouse size={22} color="#2F6B4F" weight="duotone" />
                      <div>
                        <p className="va-label">Plant-ready reagents</p>
                        <p className="va-body">Activated carbon, hydrated lime, caustic soda, cyanide grades, and acid lines.</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
                      <ShieldCheck size={22} color="#2F6B4F" weight="duotone" />
                      <div>
                        <p className="va-label">Controlled handling</p>
                        <p className="va-body">Clear product specs for cyanide, acids, and oxidizers with quote-based fulfilment.</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
                      <Truck size={22} color="#2F6B4F" weight="duotone" />
                      <div>
                        <p className="va-label">Site delivery planning</p>
                        <p className="va-body">Coordinate volumes, packaging, and lead times with your operations calendar.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={100}>
                <div className="va-feature-panel accent">
                  <p className="va-label" style={{ color: 'var(--va-accent)' }}>Supply rhythm</p>
                  <h3 className="va-h3">From quote to dispatch without the catalogue noise.</h3>
                  <p className="va-body">
                    Tell us the product line, purity, and destination. We confirm availability and packaging for your site.
                  </p>
                  {supplyAnim ? <LottieBlock animationData={supplyAnim} className="va-lottie-wrap" /> : null}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="va-section">
          <div className="va-container">
            <Reveal>
              <div className="va-section-head">
                <h2 className="va-h2">Core catalogue</h2>
                <p className="va-lede">
                  A concise set of mining chemicals and assay materials. Open the shop for the full list and category filters.
                </p>
              </div>
            </Reveal>
            <div className="va-product-preview">
              {preview.map((product, index) => (
                <Reveal key={product.id} delay={index * 60}>
                  <article className="va-product-chip">
                    <span className="meta">{product.category}</span>
                    <h3 className="va-h3" style={{ fontSize: '1.05rem' }}>
                      {product.name}
                    </h3>
                    <p className="va-body">{product.description}</p>
                  </article>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div style={{ marginTop: '2rem' }}>
                <button type="button" className="va-btn va-btn-primary" onClick={() => navigate('/shop')}>
                  Open full shop
                  <span className="va-btn-icon">
                    <ArrowUpRight size={14} weight="bold" />
                  </span>
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="va-section" style={{ paddingTop: 0 }}>
          <div className="va-container">
            <Reveal>
              <div className="va-cta-band">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', maxWidth: '36rem' }}>
                  <h2 className="va-h2">Need cyanide, lime, carbon, or caustic on a schedule?</h2>
                  <p className="va-lede">
                    Send your bill of materials. We respond with availability, packaging options, and lead times.
                  </p>
                </div>
                <button type="button" className="va-btn va-btn-primary" onClick={() => setQuoteOpen(true)}>
                  Request a quote
                  <span className="va-btn-icon">
                    <ArrowUpRight size={14} weight="bold" />
                  </span>
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <QuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
};

export default Home;
